'use strict';

let base = require('../product/base');

/**
 * Pack variation dropdown: syncs hidden `select.product__variation-select` with custom pack UI.
 * Keyboard/tab behaviour mirrors countrySelector.js: MutationObserver keeps option tabindex in
 * sync with is-open; arrows walk enabled options; Enter/Space select; Tab can enter the open list.
 */

function packTypeIconClass(type, isNespressoCompatible = false) {
	let t = String(type || '').toLowerCase();
	if (t === 'packtype_capsules' && isNespressoCompatible) {
		return 'icon-capsule-NCC';
	}
	if (t === 'packtype_capsules') {
		return 'icon-capula-fill';
	}
	if (t === 'packtype_tab') {
		return 'icon-tab';
	}
	if (t === 'packtype_tin') {
		return 'icon-coffee-can-2-fill';
	}
	if (t === 'packtype_softpack') {
		return 'icon-coffe-pack-fill';
	}
	if (t === 'packtype_pods') {
		return 'icon-cialda-fill';
	}
	if (t === 'packtype_freshpack') {
		return 'icon-flavia';
	}
	if (t === 'packtype_can' || t === 'packtype_sachet' || t === 'packtype_bottle' || t === 'packtype_otherfoodformat') {
		return 'icon-food';
	}
	return '';
}

function packTypeIconHtml(type, isNespressoCompatible) {
	let iconClass = packTypeIconClass(type, isNespressoCompatible);
	return iconClass ? '<span class="' + iconClass + '" aria-hidden="true"></span>' : '';
}

function findByAttrValue($elements, value) {
	return $elements.filter(function () {
		return $(this).attr('data-attr-value') === value;
	});
}

/**
 * @param {JQuery} $dd
 * @returns {JQuery}
 */
function getPackOptions($dd) {
	return $dd.find('.product__pack-select-option:not(:disabled)');
}

/**
 * @param {JQuery} $dd
 */
function syncPackOptionsTabIndex($dd) {
	let open = $dd.hasClass('is-open');
	$dd.find('.product__pack-select-option').each(function () {
		let $opt = $(this);
		if (open && !$opt.prop('disabled')) {
			$opt.attr('tabindex', '0');
		} else {
			$opt.attr('tabindex', '-1');
		}
	});
}

/**
 * @param {JQuery} $dd
 */
function syncPackToggleA11y($dd) {
	let $toggle = $dd.find('.product__pack-select-toggle');
	let $select = $dd.find('select.product__variation-select');
	let labelText = $.trim($dd.find('[data-pack-label]').first().text());
	let baseLabel = $select.attr('aria-label') || 'Pack size';
	let composed = labelText ? baseLabel + ', ' + labelText : baseLabel;

	$toggle.removeAttr('tabindex aria-disabled').attr('aria-label', composed);
}

/**
 * @param {JQuery} $dd
 */
function syncPackDropdownA11y($dd) {
	syncPackOptionsTabIndex($dd);
	syncPackToggleA11y($dd);
}

/**
 * @param {JQuery} $dd
 */
function closePackDropdown($dd) {
	$dd.removeClass('is-open');
	$dd.find('.product__pack-select-toggle').attr('aria-expanded', 'false');
	syncPackOptionsTabIndex($dd);
}

/**
 * @param {JQuery} $optBtn
 */
function applyPackOptionSelection($optBtn) {
	if (!$optBtn.length) {
		return;
	}

	let url = $optBtn.attr('data-url');
	if (!url) {
		return;
	}

	let $dd = $optBtn.closest('[data-pack-select]');
	let $select = $dd.find('select.product__variation-select');
	$select.val(url);
	if ($select.val() !== url) {
		$select
			.find('option')
			.filter(function () {
				return $(this).val() === url;
			})
			.prop('selected', true);
	}

	closePackDropdown($dd);

	if ($dd.closest('[data-bundle-product-card]').length) {
		$select.trigger('bundle:variation', [url]);
		return;
	}

	let $productContainer = $dd.closest('.product-detail');
	if (!$productContainer.length) {
		$productContainer = $dd.closest('.set-item');
	}
	if ($productContainer.length) {
		base.attributeSelect(url, $productContainer);
	} else {
		$select.trigger('change');
	}

	$dd.find('.product__pack-select-toggle').focus();
}

/**
 * @param {JQuery} $dd
 * @returns {JQuery}
 */
function focusFirstPackOption($dd) {
	let $options = getPackOptions($dd);
	let $selected = $options.filter('.is-selected').first();
	let $target = $selected.length ? $selected : $options.first();
	if ($target.length) {
		$target.focus();
	}
	return $target;
}

/**
 * @param {JQuery} $dd
 * @returns {JQuery}
 */
function ensurePackOpenAndOptions($dd) {
	if (!$dd.hasClass('is-open')) {
		$dd.addClass('is-open');
		$dd.find('.product__pack-select-toggle').attr('aria-expanded', 'true');
	}
	syncPackOptionsTabIndex($dd);
	return getPackOptions($dd);
}

/**
 * @param {JQuery} $dd
 */
function observePackDropdown($dd) {
	let el = $dd[0];
	if (!el || el._lvzPackDdObserved) {
		return;
	}
	el._lvzPackDdObserved = true;
	syncPackOptionsTabIndex($dd);
	let obs = new MutationObserver(function () {
		syncPackOptionsTabIndex($dd);
	});
	obs.observe(el, { attributes: true, attributeFilter: ['class'] });
}

/**
 * @param {JQuery} [$root]
 */
function initPackSelectA11y($root) {
	let $ctx = $root && $root.length ? $root : $(document);
	$ctx.find('[data-pack-select]').each(function () {
		let $dd = $(this);
		observePackDropdown($dd);
		syncPackDropdownA11y($dd);
	});
}

function syncPackSelectUI($root) {
	let $ctx = $root && $root.length ? $root : $(document);
	$ctx.find('[data-pack-select]').each(function () {
		let $dd = $(this);
		let $select = $dd.find('select.product__variation-select');
		let $opt = $select.find('option:selected');
		let selectedValue = $select.val();
		let labelText = $.trim($opt.text());
		$dd.find('[data-pack-label]').text(labelText);

		let $iconWrap = $dd.find('[data-pack-icon-wrap]');
		let url = $opt.val();
		if (!url) {
			$iconWrap.empty();
			syncPackDropdownA11y($dd);
			return;
		}
		// Read the attributes: updatePackSelectValues rewrites them and .data() keeps the first value.
		let packType = $opt.attr('data-pack-type') || '';
		let isNespressoCompatible = $opt.attr('data-is-nespresso-compatible') === 'true';
		$iconWrap.html(packTypeIconHtml(packType, isNespressoCompatible));

		let attrValue = $opt.attr('data-attr-value');
		let $selectedBtn = attrValue ? findByAttrValue($dd.find('.product__pack-select-option'), attrValue) : $();
		if (!$selectedBtn.length) {
			$selectedBtn = $dd.find('.product__pack-select-option.is-selected').first();
		}
		$dd.find('.product__pack-select-toggle').toggleClass('is-unavailable', $selectedBtn.hasClass('is-unavailable'));
		syncPackDropdownA11y($dd);
	});
}

function updatePackSelectValues(attr, $root) {
	let $select = $root.find('select.product__variation-select[data-attr-id="' + attr.id + '"]').first();
	let $dd = $select.closest('[data-pack-select]');
	if (!$select.length || !$dd.length) {
		return;
	}

	let isNespresso = function (attrValue) {
		return !!attrValue.isNespressoCompatible;
	};

	attr.values.forEach(function (attrValue) {
		let $opt = findByAttrValue($select.find('option'), attrValue.value);
		if (!$opt.length) {
			return;
		}
		if (attrValue.url) {
			$opt.val(attrValue.url);
		}
		$opt.attr('data-attr-value', attrValue.value)
			.attr('data-pack-type', attrValue.type || '')
			.attr('data-is-nespresso-compatible', isNespresso(attrValue) ? 'true' : 'false')
			.prop('disabled', false)
			.prop('selected', !!attrValue.selected)
			.text(attrValue.displayValue || '');

		let $optBtn = findByAttrValue($dd.find('.product__pack-select-option'), attrValue.value);
		if (!$optBtn.length) {
			return;
		}
		$optBtn
			.toggleClass('is-selected', !!attrValue.selected)
			.toggleClass('is-unavailable', !attrValue.selectable)
			.removeClass('is-disabled')
			.attr('aria-selected', attrValue.selected ? 'true' : 'false')
			.prop('disabled', false);
		if (attrValue.url) {
			$optBtn.attr('data-url', attrValue.url);
		}
		$optBtn
			.attr('data-pack-type', attrValue.type || '')
			.attr('data-label', attrValue.displayValue || '')
			.attr('data-is-nespresso-compatible', isNespresso(attrValue) ? 'true' : 'false');
		$optBtn.find('.product__pack-select-option-text').text(attrValue.displayValue || '');
		$optBtn
			.find('.product__pack-select-checkwrap')
			.html(attrValue.selected ? '<span class="icon-check product__pack-select-check"></span>' : '');

		let packType = attrValue.type || '';
		let $iconWrap = $optBtn.find('.dd__item-icon').first();
		if (!packType) {
			$iconWrap.remove();
		} else if ($iconWrap.length) {
			$iconWrap.html(packTypeIconHtml(packType, isNespresso(attrValue)));
		} else {
			$('<span class="dd__item-icon" aria-hidden="true"></span>')
				.html(packTypeIconHtml(packType, isNespresso(attrValue)))
				.prependTo($optBtn);
		}
	});

	syncPackDropdownA11y($dd);
}

function handlePackSelectTabKey(e, $dd, $toggle) {
	let activeElTab = document.activeElement;
	let $options = getPackOptions($dd);

	if ($dd.hasClass('is-open') && $toggle.is(activeElTab) && !e.shiftKey) {
		e.preventDefault();
		focusFirstPackOption($dd);
		return;
	}

	if ($dd.hasClass('is-open') && $options.index(activeElTab) !== -1) {
		return;
	}

	if ($dd.hasClass('is-open')) {
		closePackDropdown($dd);
	}
}

function handlePackSelectEscapeKey(e, $dd, $toggle) {
	if (!$dd.hasClass('is-open')) {
		return;
	}
	e.preventDefault();
	closePackDropdown($dd);
	$toggle.focus();
}

function handlePackSelectArrowKeys(e, $dd, $toggle, key) {
	if ($(e.target).hasClass('product__pack-select-option')) {
		return;
	}

	e.preventDefault();

	if (key === 40) {
		ensurePackOpenAndOptions($dd);
		focusFirstPackOption($dd);
		return;
	}

	if ($toggle.is(document.activeElement) && $dd.hasClass('is-open')) {
		closePackDropdown($dd);
	}
}

module.exports = function initProductPackSelect() {
	// Re-sync the UI on every call (new tiles/PDP DOM). The delegated handlers are bound
	// through the .lvzPackSelect namespace and cleared first, so running this initializer
	// several times per page (productDetail, search, subscriptions, carousels — or webpack
	// scope hoisting inlining this module more than once) can never double-bind them and
	// turn one pack-option click into two Product-Variation calls.
	syncPackSelectUI();
	initPackSelectA11y();

	$(document).off('.lvzPackSelect');
	$('body').off('.lvzPackSelect');

	$('body').on('search:updateDom.lvzPackSelect', function () {
		syncPackSelectUI();
		initPackSelectA11y();
	});

	$(document).on('change.lvzPackSelect', '[data-pack-select] select.product__variation-select', function () {
		syncPackSelectUI($(this).closest('[data-pack-select]'));
	});

	// dropdown.js toggles is-open then fires lavazza:ddAfterToggle on the toggle.
	$(document).on('lavazza:ddAfterToggle.lvzPackSelect', '[data-pack-select] .product__pack-select-toggle', function () {
		let $dd = $(this).closest('[data-pack-select]');
		syncPackOptionsTabIndex($dd);
		if ($dd.hasClass('is-open')) {
			focusFirstPackOption($dd);
		}
	});

	$(document).on('keydown.lvzPackSelect', '.product__pack-select-option', function onPackSelectOptionKeydown(e) {
		let key = e.key;
		if (key !== 'Enter' && key !== ' ' && key !== 'ArrowDown' && key !== 'ArrowUp') {
			return;
		}
		e.preventDefault();
		e.stopPropagation();

		let $opt = $(this);
		let $dd = $opt.closest('[data-pack-select]');
		let $options = getPackOptions($dd);
		let idx = $options.index(this);

		if (key === 'Enter' || key === ' ') {
			applyPackOptionSelection($opt);
			return;
		}

		if (key === 'ArrowDown' && idx < $options.length - 1) {
			$options.eq(idx + 1).focus();
			return;
		}

		if (key === 'ArrowUp' && idx === 0) {
			closePackDropdown($dd);
			$dd.find('.product__pack-select-toggle').focus();
			return;
		}

		if (key === 'ArrowUp' && idx > 0) {
			$options.eq(idx - 1).focus();
		}
	});

	$(document).on('keydown.lvzPackSelect', '[data-pack-select]', function onPackSelectKeydown(e) {
		let key = e.which;
		let $dd = $(this);
		let $toggle = $dd.find('.product__pack-select-toggle');

		if (key === 9) {
			handlePackSelectTabKey(e, $dd, $toggle);
			return;
		}

		if (key === 27) {
			handlePackSelectEscapeKey(e, $dd, $toggle);
			return;
		}

		if (key !== 38 && key !== 40) {
			return;
		}

		handlePackSelectArrowKeys(e, $dd, $toggle, key);
	});

	$(document).on('focusout.lvzPackSelect', '[data-pack-select]', function onPackSelectFocusout() {
		let $dd = $(this);
		window.setTimeout(function () {
			if (!$dd.hasClass('is-open')) {
				return;
			}
			let active = document.activeElement;
			if (active && $dd[0] && $dd[0].contains(active)) {
				return;
			}
			closePackDropdown($dd);
		}, 0);
	});

	$(document).on('click.lvzPackSelect', '.product__pack-select-option', function (e) {
		e.preventDefault();
		applyPackOptionSelection($(this));
	});

	$('body').on('product:afterAttributeSelect.lvzPackSelect', function (e, response) {
		if (!response || !response.data || !response.data.product || !response.container || !response.container.length) {
			return;
		}
		let $container = response.container;
		let product = response.data.product;
		let $pdpRoot = $container.hasClass('product-detail') ? $container : $container.closest('.product-detail');
		let $scope = $pdpRoot.length ? $pdpRoot : $container;

		if ($pdpRoot.length && product.productName) {
			$pdpRoot.find('h1.product-name').first().text(product.productName);
		}
		if ($pdpRoot.length && product.shortDescription) {
			$pdpRoot.find('.product-data__short-description').first().text(product.shortDescription);
		}

		if (!$scope.find('[data-pack-select]').length) {
			return;
		}

		if (product.variationAttributes && product.variationAttributes.length) {
			product.variationAttributes.forEach(function (attr) {
				updatePackSelectValues(attr, $scope);
			});
		}
		syncPackSelectUI($scope);
		initPackSelectA11y($scope);
	});
};

module.exports.syncPackSelectUI = syncPackSelectUI;
module.exports.updatePackSelectValues = updatePackSelectValues;
module.exports.initPackSelectA11y = initPackSelectA11y;
